import React, {ChangeEvent, useEffect, useState} from 'react';
import {Button, message} from 'antd';

import ParcelsDownloadModal from "./ParcelsDownloadModal";
import {useMutation, useQuery} from '@apollo/client';
import {getUserInfo} from "../../../../utils/auth";
import {GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS, AllSettlementClientResponse} from '../graphql/queries/getAllSettlementClient';
import { GET_FEE_ITEM_LIST_FOR_OPTIONS, AllFeeItemResponse} from '../graphql/queries/getAllFeeItem';
import { CREATE_VOUCHER_CLEARING_DOWNLOAD_TASK, VoucherClearingDownloadTaskResponse } from '../graphql/mutations/createVoucherClearingDownloadTask';
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import BatchInputFilterItem from "../../../delivery/components/BatchInputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import DateRangeFilterItem from "../../../delivery/components/DateRangeFilterItem";
import {
    getVoucherClearingUserTypeOptions,
    voucherClearingStatusOptions,
    validateVoucherClearingFilters,
} from "../config/filterOptions";
import {formatTimeRangeToISO} from "../../../../utils/dateTimeUtils";
import { VoucherClearingFilterType, VoucherClearingType } from '../graphql/types/VoucherClearingType';
import {SelectOptionType} from 'graphql/types/SelectOptionType'
import {HasResource} from "../../../../components/HasResource";
import {ResourceCode} from "../../../../constants/resourceCodes";
interface VoucherClearingFilterProps {
    filters: VoucherClearingFilterType;
    setFilters: (filters: VoucherClearingFilterType) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
}

const VoucherClearingFilter: React.FC<VoucherClearingFilterProps> = ({
                                                       filters,
                                                       setFilters,
                                                       handleFilter,
                                                       handleResetFilters,
                                                   }) => {
    const userRole = getUserInfo()?.account?.role?.name || '';
    const [feeItemOptions, setFeeItemOptions] = useState<SelectOptionType[]>([]);
    const [settlementClientOptions, setSettlementClientOptions] = useState<SelectOptionType[]>([]);
    // UserType 联动：订单用户 → Client roleType=0 / Settlement Code 1001001；DSP → roleType=1 / 1001002
    const hasUserType = Boolean(filters.userType);
    const settlementClientRoleType = filters.userType === 'dsp' ? 1 : filters.userType === 'order' ? 0 : null;
    const feeItemBusinessScenarioCode = filters.userType === 'dsp' ? 1001002 : filters.userType === 'order' ? 1001001 : null;

    const {data: settlementClientList} = useQuery<AllSettlementClientResponse>(GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS, {
        skip: !hasUserType,
        variables: {
            roleType: settlementClientRoleType
        }
    });
    const {data: feeItemList} = useQuery<AllFeeItemResponse>(GET_FEE_ITEM_LIST_FOR_OPTIONS, {
        skip: !hasUserType,
        variables: {
            businessScenarioCode: feeItemBusinessScenarioCode
        }
    });
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
    const DOWNLOAD_COOLDOWN = 5000; // 5秒冷却时间

    // 下载完成模态框状态
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState<string>('');

    // 使用mutation来创建下载任务
    const [createVoucherClearingDownloadTask] = useMutation<VoucherClearingDownloadTaskResponse>(CREATE_VOUCHER_CLEARING_DOWNLOAD_TASK);

    // 从localStorage获取上次下载时间，页面刷新后仍然有效
    const getLastDownloadTime = (): number => {
        const stored = localStorage.getItem('voucherClearingDownloadLastTime');
        return stored ? parseInt(stored, 10) : 0;
    };

    const setLastDownloadTime = (time: number) => {
        localStorage.setItem('voucherClearingDownloadLastTime', time.toString());
    };

    useEffect(() => {
        if (!hasUserType) {
            setFeeItemOptions([]);
            setSettlementClientOptions([]);
            return;
        }
        if (feeItemList) {
            const list = Array.isArray(feeItemList.allOptions) ? feeItemList.allOptions : [feeItemList.allOptions];
            setFeeItemOptions(list);
        }
        if (settlementClientList) {
            const list = Array.isArray(settlementClientList.allOptions) ? settlementClientList.allOptions : [settlementClientList.allOptions];
            setSettlementClientOptions(list);
        }
    }, [hasUserType, feeItemList, settlementClientList]);

    // 冷却时间倒计时（页面刷新后仍然有效）
    useEffect(() => {
        const checkCooldown = () => {
            const lastTime = getLastDownloadTime();
            if (lastTime > 0) {
                const currentTime = Date.now();
                const remaining = Math.max(0, DOWNLOAD_COOLDOWN - (currentTime - lastTime));
                const remainingSeconds = Math.ceil(remaining / 1000);
                setCooldownRemaining(remainingSeconds);

                if (remaining <= 0) {
                    setCooldownRemaining(0);
                    setDownloadLoading(false);
                    localStorage.removeItem('voucherClearingDownloadLastTime'); // 清理过期数据
                }
            } else {
                setCooldownRemaining(0);
            }
        };

        // 立即检查一次（页面刷新后）
        checkCooldown();
        // 启动定时器，每秒更新一次
        const interval = setInterval(checkCooldown, 1000);
        return () => clearInterval(interval);
    }, [DOWNLOAD_COOLDOWN]);

    // 处理时间变化的函数
    const handleBusinessTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, businessTime: dates});
    };

     const handleSettlementTimeChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, settlementTime: dates});
    };


    // 构建查询参数 - 与useVoucherClearingQuery中的buildQueryVariables保持一致
    const buildQueryFilters = (filters: VoucherClearingFilterType) => {
        const timeRanges = {
            businessTime: formatTimeRangeToISO(filters.businessTime),
            settlementTime: formatTimeRangeToISO(filters.settlementTime),
        };
        return {
            ...filters,
            ...timeRanges
        };
    };

    // Download handler function
    const handleDownloadVoucherClearing = async () => {
        const validationError = validateVoucherClearingFilters(filters);
        if (validationError) {
            message.warning(validationError);
            return;
        }

        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();

        // Check cooldown time
        if (currentTime - lastTime < DOWNLOAD_COOLDOWN) {
            const remainingTime = Math.ceil((DOWNLOAD_COOLDOWN - (currentTime - lastTime)) / 1000);
            message.warning(`Please wait ${remainingTime} seconds before trying again to avoid duplicate requests`);
            return;
        }
        // Check if already loading
        if (downloadLoading) {
            message.warning('Voucher is being generated, please do not click repeatedly');
            return;
        }

        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime); // Store to localStorage
            // 使用与filter按钮完全相同的参数构建逻辑
            const queryFilters = buildQueryFilters(filters);
            const response = await createVoucherClearingDownloadTask({
                variables: {
                    filters: queryFilters,
                }
            });
            if (response.data?.createVoucherClearingDownloadTask) {
                setGeneratedFileName(response.data?.createVoucherClearingDownloadTask);
                setDownloadCompleteModalVisible(true);
            } else {
                message.error('Failed to start report generation task');
            }
        } catch (error) {
            console.error('Create VoucherClearing Download Task Failed:', error);
            message.error('Create VoucherClearing Download Task Failed, Please Try Again Later');
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="mb-6">
            <div className="mb-4">
                <BatchInputFilterItem
                    label="Tracking"
                    value={filters.businessId || ''}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFilters({
                        ...filters,
                        businessId: e.target.value || undefined
                    })}
                    spanClassName="w-[9em]"
                    rows={2}
                />
            </div>
            <div className="grid grid-cols-3 gap-x-4 gap-y-3 mb-4">
                <ParcelFilterStyle
                    label="Client Type"
                    options={getVoucherClearingUserTypeOptions()}
                    value={filters.userType ?? undefined}
                    onChange={(value: string) => setFilters({
                        ...filters,
                        userType: value || undefined,
                        settlementClientId: undefined,
                        settlementItemCode: undefined
                    })}
                    spanClassName="w-[9em]"
                />
                <ParcelFilterStyle
                    label="Client"
                    options={settlementClientOptions}
                    value={filters.settlementClientId ?? undefined}
                    onChange={(value: string) => setFilters({...filters, settlementClientId: value || undefined})}
                    spanClassName="w-[9em]"
                    showSearch={true}
                />
                <ParcelFilterStyle
                    label="Settlement Code Name"
                    options={feeItemOptions}
                    value={filters.settlementItemCode ?? undefined}
                    onChange={(value: string) => setFilters({...filters, settlementItemCode: value || undefined})}
                    spanClassName="w-[9em]"
                    showSearch={true}
                />
                <ParcelFilterStyle
                    label="Voucher Status"
                    options={voucherClearingStatusOptions}
                    value={filters.voucherClearStatus ?? undefined}
                    onChange={(value: string) => setFilters({...filters, voucherClearStatus: value ? value : undefined})}
                    spanClassName="w-[9em]"
                />
                <DateRangeFilterItem
                    label="Business Time"
                    value={filters.businessTime}
                    onChange={handleBusinessTimeChange}
                    spanClassName="w-[9em]"
                />

                <DateRangeFilterItem
                    label="Settlement Time"
                    value={filters.settlementTime}
                    onChange={handleSettlementTimeChange}
                    spanClassName="w-[9em]"
                    required={!filters.businessId}
                />
                <InputFilterItem
                    label="Order"
                    value={filters.otherBusinessId}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({...filters, otherBusinessId: e.target.value || undefined})}
                    spanClassName="w-[9em]"
                />
            </div>

            <div className="mb-4 flex justify-between">
                <div className="space-x-4">
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_CLEARING_VOUCHER, ResourceCode.settlement.SETTLEMENT_REVENUE_CLEARING_VOUCHER]}>
                        <Button
                            type="primary"
                            onClick={handleDownloadVoucherClearing}
                            loading={cooldownRemaining > 0}
                            disabled={downloadLoading || cooldownRemaining > 0}
                        >
                            {cooldownRemaining > 0
                                ? `Download ${cooldownRemaining}s`
                                : 'Download'
                            }
                        </Button>
                    </HasResource>
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_CLEARING_VOUCHER, ResourceCode.settlement.SETTLEMENT_REVENUE_CLEARING_VOUCHER]}>
                        <Button type="primary" onClick={handleFilter}>
                            Filter
                        </Button>
                    </HasResource>
                    <Button onClick={handleResetFilters}>
                        Reset
                    </Button>
                </div>
            </div>
                
            <ParcelsDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default VoucherClearingFilter;